import React from 'react';
import { Check, Clock, AlertTriangle, RefreshCcw, XCircle, Hourglass, Copy } from 'lucide-react';

/**
 * Reusable payment-status pill with friendly labels + brand colors.
 *
 * Maps backend status codes -> human-readable labels matching the user's spec:
 *   pending             -> "Pending Stripe Confirmation"  (amber)
 *   processing          -> "Processing"                   (amber)
 *   paid / completed    -> "Payment Completed"            (green)
 *   failed              -> "Payment Failed"               (red)
 *   refunded            -> "Refunded"                     (gray)
 *   requires_action     -> "Requires Action"              (amber)
 *   expired             -> "Expired Checkout"             (gray)
 *   duplicate_pending   -> "Duplicate Pending"            (gray)
 */
const STATUS_CONFIG = {
  pending:           { label: 'Pending Stripe Confirmation', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  processing:        { label: 'Processing',                  cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Hourglass },
  paid:              { label: 'Payment Completed',           cls: 'bg-green-100 text-green-800 border-green-200', icon: Check },
  completed:         { label: 'Payment Completed',           cls: 'bg-green-100 text-green-800 border-green-200', icon: Check },
  failed:            { label: 'Payment Failed',              cls: 'bg-red-100 text-red-800 border-red-200',       icon: XCircle },
  refunded:          { label: 'Refunded',                    cls: 'bg-gray-100 text-gray-700 border-gray-200',    icon: RefreshCcw },
  requires_action:   { label: 'Requires Action',             cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertTriangle },
  expired:           { label: 'Expired Checkout',            cls: 'bg-gray-100 text-gray-700 border-gray-200',    icon: XCircle },
  duplicate_pending: { label: 'Duplicate Pending',           cls: 'bg-gray-100 text-gray-700 border-gray-200',    icon: Copy },
};

export const PAID_STATUSES = new Set(['paid', 'completed']);
export const FAILED_STATUSES = new Set(['failed', 'expired', 'duplicate_pending']);
export const PENDING_STATUSES = new Set(['pending', 'processing', 'requires_action']);
export const isPaid = (s) => PAID_STATUSES.has(s);
export const isFailed = (s) => FAILED_STATUSES.has(s);
export const isPending = (s) => PENDING_STATUSES.has(s);

export const PaymentStatusPill = ({ status, size = 'sm', testId }) => {
  const cfg = STATUS_CONFIG[status] || { label: status || 'Unknown', cls: 'bg-gray-100 text-gray-600 border-gray-200', icon: Clock };
  const Icon = cfg.icon;
  const sizeCls = size === 'lg' ? 'text-xs px-2.5 py-1' : 'text-[10px] px-1.5 py-0.5';
  return (
    <span
      className={`inline-flex items-center gap-1 font-bold uppercase tracking-wide rounded border ${cfg.cls} ${sizeCls}`}
      data-testid={testId || `payment-status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

/**
 * Big red "⚠ DO NOT RELEASE ITEM" warning shown on unpaid order detail.
 */
export const DoNotReleaseWarning = ({ status, reason }) => {
  if (isPaid(status)) return null;
  const message = isFailed(status)
    ? 'Payment was not received. Do not release this item.'
    : 'Payment is still pending Stripe confirmation. Do not release this item until payment is confirmed.';
  return (
    <div
      className="bg-red-600 text-white border-l-4 border-red-900 rounded-lg p-4 flex items-start gap-3 shadow-md"
      data-testid="do-not-release-warning"
      role="alert"
    >
      <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <h3 className="font-heading text-base font-bold uppercase tracking-wider">⚠ DO NOT RELEASE ITEM</h3>
        <p className="text-sm mt-1">{message}</p>
        {reason && <p className="text-xs mt-1 opacity-90">Reason: {reason}</p>}
      </div>
    </div>
  );
};

export default PaymentStatusPill;
